import { useCallback, useEffect, useState } from 'react';
import { FEATURE_FLAGS } from '../../constants/FEATURE_FLAG_KEYS';
import {
    getFlagUsers,
    setUserFlag,
    type FlagUsersResult,
} from '../../services/api/featureFlagService';
import { useToast } from '../../hooks/use-toast';
import ToggleableList, {
    type ToggleableListGroup,
} from '../shared/ToggleableList';
import UserTableSection from './UserTableSection';

export default function BetaFeaturesSection() {
    const { toast } = useToast();
    const [data, setData] = useState<FlagUsersResult | null>(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setData(await getFlagUsers());
        } catch {
            toast({
                title: 'Failed to load beta features',
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
    }, [toast]);

    useEffect(() => {
        load();
    }, [load]);

    const enabledFlags = FEATURE_FLAGS.filter((flag) =>
        data?.departmentBetaFeatures.includes(flag.key)
    );
    const users = data?.users ?? [];

    const groups: ToggleableListGroup[] = enabledFlags.map((flag) => ({
        key: flag.key,
        label: flag.label,
        description: flag.description,
        items: users.map((u) => ({
            key: u._id,
            label: u.name || u.email,
            description: u.name ? u.email : undefined,
        })),
        selected: users
            .filter((u) => u.betaFeatures.includes(flag.key))
            .map((u) => u._id),
    }));

    const handleToggle = async (
        flag: string,
        userId: string,
        enabled: boolean
    ) => {
        const betaFeatures = await setUserFlag(userId, flag, enabled);
        setData((prev) =>
            prev
                ? {
                      ...prev,
                      users: prev.users.map((u) =>
                          u._id === userId ? { ...u, betaFeatures } : u
                      ),
                  }
                : prev
        );
    };

    // Departments with no enrolled beta features have nothing to manage
    // here — hide the section (also while loading, to avoid a spinner that
    // vanishes for the common no-flags case).
    if (loading || enabledFlags.length === 0) return null;

    return (
        <div className="mt-10">
            <UserTableSection
                title="BETA feature access"
                count={enabledFlags.length}
                description="Choose which of your users can try beta features enabled for your facility."
            >
                <ToggleableList
                    groups={groups}
                    onToggle={handleToggle}
                    emptyText="No beta features are enabled for your facility yet."
                    countLabel="users enrolled"
                />
            </UserTableSection>
        </div>
    );
}
